import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import css from './LinkTalentModal.module.css';
import {
  fetchTalentDetailsUsingISDK,
  syncTalentInProgressSelector,
  syncTalentProfileOwnList,
  talentDetailsSelector,
} from '../LinkTalentPage.duck';
import { H2, Icons, IconSpinner, Modal, PrimaryButton, SecondaryButton } from '../../../components';
import { isArrayLength } from '../../../util/genericHelpers';
import { manageDisableScrolling } from '../../../ducks/ui.duck';
import { UserType } from '../../../util/enums';
import { showErrorToast } from '../../../util/toast';
import { emailFormatValid } from '../../../util/validators';

// Custom debounce implementation (or use lodash if preferred)
const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

const MAX_EMAIL_LIMIT = 50;

const LinkTalentModal = ({
  isOpen,
  onClose,
  nonInvitableUserIds,
  currentUserListing,
  userType,
  isLinkTalentModal,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { fetchTalentDetailsInProgress } = useSelector(talentDetailsSelector);
  const syncTalentInProgress = useSelector(syncTalentInProgressSelector);

  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [error, setError] = useState('');

  // Memoized derived values
  const listingTitle = currentUserListing?.attributes?.title || 'Unnamed Listing';
  const listingId = currentUserListing?.id?.uuid || null;
  const submitDisabled = selectedEntries.length === 0 || syncTalentInProgress;
  // Handle search with debouncing
  const handleSearch = useCallback(
    debounce(async term => {
      if (!term.trim()) {
        setSuggestions([]);
        setError('');
        return;
      }

      const trimmedTerm = term.trim().toLowerCase();
      const isAlreadySelected = selectedEntries.some(e => e.value.toLowerCase() === trimmedTerm);
      if (isAlreadySelected) {
        setError('This entry has already been added.');
        setSuggestions([]);
        showErrorToast('This entry has already been added.');
        return;
      }

      if (selectedEntries.length >= MAX_EMAIL_LIMIT) {
        setError(`You can only invite up to ${MAX_EMAIL_LIMIT} entries.`);
        setSuggestions([]);
        showErrorToast(`Maximum limit of ${MAX_EMAIL_LIMIT} entries reached.`);
        return;
      }

      try {
        const validateEmail = emailFormatValid('Invalid email format.');
        const validationError = validateEmail(trimmedTerm);

        if (validationError) {
          setError(validationError);
          setSuggestions([]);
          showErrorToast(validationError);
          return;
        }

        const response = await dispatch(fetchTalentDetailsUsingISDK({ email: trimmedTerm }));

        if (response?.userType !== UserType.TALENT) {
          setError('This user is not registered as a talent.');
          setSuggestions([]);
          showErrorToast('This user is not a talent.');
          return;
        }
        if (response?.associatedListingId) {
          setError('This user is already linked to one or more listings.');
          setSuggestions([]);
          showErrorToast('This user is already linked to one or more listings.');
          return;
        }
        if (response?.associatedListingId === listingId) {
          setError('This user is already linked to this listing.');
          setSuggestions([]);
          showErrorToast('This user is already linked to this listing.');
          return;
        }
        if (response?.email && response?.firstName && response?.lastName) {
          const newEntry = {
            type: 'email',
            value: response.email,
            displayName: `${response.firstName} ${response.lastName}`.trim(),
            talentId: response.talentId || null,
          };

          setSuggestions(prev => {
            const exists = prev.some(e => e.value.toLowerCase() === newEntry.value.toLowerCase());
            return exists ? prev : [...prev, newEntry];
          });
          setError('');
        } else {
          setSuggestions([]);
          setError('No matching user found or incomplete user data.');
          showErrorToast('No matching user found.');
        }
      } catch (e) {
        setSuggestions([]);
        setError('Error fetching user details. Please try again.');
        showErrorToast('An error occurred while fetching user details.');
      }
    }, 1500),
    [dispatch, selectedEntries, MAX_EMAIL_LIMIT, UserType]
  );

  // Handle adding an entry from suggestions
  const handleAddEntry = useCallback(entry => {
    setSelectedEntries(prev => [...prev, entry]);
    setSuggestions([]);
    setInputValue('');
    setError('');
  }, []);

  // Handle removing a selected entry
  const handleRemoveEntry = useCallback(entryToRemove => {
    setSelectedEntries(prev => prev.filter(e => e.value !== entryToRemove.value));
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    try {
      const filteredEntries = selectedEntries.filter(
        entry => !nonInvitableUserIds.includes(entry.userId)
      );

      await dispatch(
        syncTalentProfileOwnList({
          associatedUsers: filteredEntries,
          listingId,
          associatedListingCategory: userType,
        })
      );
      onClose();
    } catch (error) {}
  }, [selectedEntries, nonInvitableUserIds]); // Ensure all dependencies are included

  // Effect to reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
      setSuggestions([]);
      setSelectedEntries([]);
      setError('');
    }
  }, [isOpen]);
  return (
    <Modal
      id="LinkTalentModal"
      isOpen={isOpen}
      onClose={onClose}
      usePortal
      onManageDisableScrolling={(componentId, disable) =>
        dispatch(manageDisableScrolling(componentId, disable))
      }
      shouldShowIcon={<Icons name="userPlus" />}
      aria-labelledby="link-talent-modal-title"
      isLinkTalentModal={isLinkTalentModal}
    >
      <div className={css.invitationModalContent}>
        <div className={css.invitationModalHead}>
          <H2 as="h2" id="link-talent-modal-title">
            <FormattedMessage
              id="LinkTalentModal.sendCredentialsHeading"
              values={{ listingTitle }}
            />
          </H2>
          <p>
            <FormattedMessage
              id="LinkTalentModal.sendCredentialsDescription"
              values={{ listingTitle }}
            />
          </p>
        </div>
        <div className={css.inputContainer}>
          <label htmlFor="email-input" className={css.label}>
            {intl.formatMessage({ id: 'LinkTalentModal.emailLabel' })}
          </label>
          <div className={css.inputIconSec}>
            <input
              id="email-input"
              type="email"
              value={inputValue}
              className={css.inputField}
              placeholder={intl.formatMessage({ id: 'LinkTalentModal.inputPlaceholder' })}
              onChange={e => {
                setInputValue(e.target.value);
                handleSearch(e.target.value);
              }}
              required
              aria-describedby={error ? 'error-message' : undefined}
              aria-invalid={!!error}
            />
            {inputValue && (
              <button
                type="button"
                className={css.clearButton}
                onClick={() => {
                  setInputValue('');
                  setSuggestions([]);
                  setError('');
                }}
                aria-label="Clear input"
              >
                <Icons name="cross" />
              </button>
            )}
            {isArrayLength(suggestions) && (
              <div className={css.suggestionsList} role="listbox">
                {suggestions.map((entry, i) => (
                  <div
                    key={i}
                    className={css.suggestionItem}
                    onClick={() => handleAddEntry(entry)}
                    role="option"
                    aria-selected={selectedEntries.some(e => e.value === entry.value)}
                  >
                    {entry.displayName || entry.value}
                  </div>
                ))}
              </div>
            )}
          </div>
          {error && (
            <div id="error-message" className={css.errorMessage} role="alert">
              {error}
            </div>
          )}
        </div>
        <div className={css.selectedEntriesList} role="list">
          {selectedEntries.map((entry, i) => (
            <div key={i} className={css.entryPill} role="listitem">
              <span>{entry.displayName || entry.value}</span>
              <button
                type="button"
                className={css.remove}
                onClick={() => handleRemoveEntry(entry)}
                aria-label={`Remove ${entry.displayName || entry.value}`}
              >
                <Icons name="cross" />
              </button>
            </div>
          ))}
        </div>
        {fetchTalentDetailsInProgress && (
          <div className={css.loader} aria-live="polite">
            <IconSpinner />
          </div>
        )}
        <div className={css.actionButtons}>
          <SecondaryButton type="button" onClick={onClose}>
            <FormattedMessage id="LinkTalentModal.cancel" />
          </SecondaryButton>
          <PrimaryButton
            type="button"
            inProgress={syncTalentInProgress}
            onClick={handleSubmit}
            disabled={submitDisabled}
            aria-label={submitDisabled ? 'Submit disabled' : 'Submit invitations'}
          >
            <FormattedMessage id="LinkTalentModal.done" />
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default LinkTalentModal;
